import { useState, useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BusinessShareholder } from '../../types'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  shareholder: BusinessShareholder | null
  basePath: string
}

export function CapTableShareholderSheet({ open, onOpenChange, businessId, shareholder, basePath }: Props) {
  const [apiError, setApiError] = useState<string | null>(null)

  const { data, setData, post, patch, processing, reset, clearErrors } = useForm({
    name: '',
    email: '',
    shareholder_type: 'individual',
    notes: ''
  })

  useEffect(() => {
    if (open) {
      if (shareholder) {
        setData({
          name: shareholder.name,
          email: shareholder.email || '',
          shareholder_type: shareholder.shareholder_type,
          notes: shareholder.notes || ''
        })
      } else { reset() }
      setApiError(null); clearErrors()
    }
  }, [open, shareholder])

  function handleClose(success: boolean) {
    onOpenChange(false)
    if (!success) { reset(); clearErrors() }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setApiError(null)
    const resourcePath = `${basePath}/businesses/${businessId}/shareholders`
    const options = { data: { shareholder: data }, onSuccess: () => handleClose(true), onError: (errors: Record<string, string>) => setApiError(Object.values(errors).join(', ')) }
    if (shareholder?.id) {
      patch(`${resourcePath}/${shareholder.id}`, options)
    } else {
      post(resourcePath, options)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="mb-6"><SheetTitle>{shareholder ? 'Edit Shareholder' : 'Add Shareholder'}</SheetTitle></SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4 px-4">
          {apiError && <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">{apiError}</div>}
          <div className="space-y-2">
            <Label htmlFor="sh_name">Name <span className="text-destructive">*</span></Label>
            <Input
              id="sh_name"
              placeholder="e.g. John Doe"
              value={data.name}
              onChange={e => setData('name', e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sh_email">Email</Label>
            <Input
              id="sh_email"
              type="email"
              placeholder="john@example.com"
              value={data.email}
              onChange={e => setData('email', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sh_type">Type <span className="text-destructive">*</span></Label>
            <Select value={data.shareholder_type} onValueChange={v => setData('shareholder_type', v)}>
              <SelectTrigger id="sh_type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="entity">Entity</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="sh_notes">Notes</Label>
            <Textarea
              id="sh_notes"
              placeholder="Optional notes..."
              value={data.notes}
              onChange={e => setData('notes', e.target.value)}
              rows={3}
            />
          </div>
          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-8">
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleClose(false)}>Cancel</Button>
              <Button type="submit" className="flex-1" disabled={processing}>{processing ? 'Saving...' : shareholder ? 'Update' : 'Add Shareholder'}</Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
